import React, { useState, useEffect } from 'react';
import { ref, get, update } from 'firebase/database';
import {useAuth} from "../../contexts/AuthContext";
import {database} from "../../services/firebase";

export default function PlayerAnnouncements() {
    const { currentUser } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [readAnnouncements, setReadAnnouncements] = useState([]);

    useEffect(() => {
        async function fetchAnnouncements() {
            try {
                // Récupérer les annonces
                const announcementsRef = ref(database, 'announcements');
                const snapshot = await get(announcementsRef);

                if (!snapshot.exists()) {
                    setAnnouncements([]);
                    setLoading(false);
                    return;
                }

                const announcementsData = snapshot.val();
                const now = new Date();

                // Filtrer les annonces pertinentes pour le joueur
                const relevantAnnouncements = Object.entries(announcementsData)
                    .map(([id, data]) => ({
                        id,
                        ...data
                    }))
                    .filter(announcement => {
                        // Seulement les annonces publiées
                        if (!announcement.published) return false;

                        // Vérifier si l'annonce n'est pas expirée
                        if (announcement.expiresAt) {
                            const expiryDate = new Date(announcement.expiresAt);
                            if (expiryDate < now) return false;
                        }

                        // Vérifier que l'audience correspond (tous ou joueurs)
                        return announcement.targetAudience === 'all' || announcement.targetAudience === 'players';
                    })
                    .sort((a, b) => {
                        // Trier d'abord par importance
                        if (a.importance === 'important' && b.importance !== 'important') return -1;
                        if (a.importance !== 'important' && b.importance === 'important') return 1;

                        // Puis par date de publication (plus récent en premier)
                        const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(0);
                        const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(0);
                        return dateB - dateA;
                    });

                setAnnouncements(relevantAnnouncements);

                // Récupérer les annonces lues par l'utilisateur
                if (currentUser) {
                    const userReadRef = ref(database, `users/${currentUser.uid}/readAnnouncements`);
                    const readSnapshot = await get(userReadRef);

                    if (readSnapshot.exists()) {
                        setReadAnnouncements(readSnapshot.val() || []);
                    }
                }

            } catch (error) {
                console.error("Erreur lors de la récupération des annonces:", error);
                setError("Impossible de charger les actualités.");
            } finally {
                setLoading(false);
            }
        }

        fetchAnnouncements();
    }, [currentUser]);

    // Marquer une annonce comme lue
    const markAsRead = async (announcementId) => {
        if (!currentUser) return;

        try {
            // Ajouter l'ID à la liste des annonces lues
            if (!readAnnouncements.includes(announcementId)) {
                const updatedReadList = [...readAnnouncements, announcementId];
                const userReadRef = ref(database, `users/${currentUser.uid}/readAnnouncements`);
                await update(userReadRef, updatedReadList);
                setReadAnnouncements(updatedReadList);
            }
        } catch (error) {
            console.error("Erreur lors du marquage de l'annonce:", error);
        }
    };

    // Formater la date
    const formatDate = (dateString) => {
        if (!dateString) return '';

        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Si aucune annonce à afficher
    if (!loading && announcements.length === 0) {
        return null; // Ne rien afficher si pas d'annonces
    }

    return (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
            <div className="bg-indigo-600 px-4 py-3">
                <h2 className="text-lg font-medium text-white flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                    Annonces
                    {announcements.filter(a => !readAnnouncements.includes(a.id)).length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {announcements.filter(a => !readAnnouncements.includes(a.id)).length} nouvelle(s)
                        </span>
                    )}
                </h2>
            </div>

            <div className="divide-y divide-gray-200">
                {loading ? (
                    <div className="p-4 text-center">
                        <div className="animate-spin inline-block h-6 w-6 border-t-2 border-b-2 border-indigo-500 rounded-full"></div>
                        <p className="mt-2 text-sm text-gray-500">Chargement des annonces...</p>
                    </div>
                ) : error ? (
                    <div className="p-4 text-center text-sm text-red-500">{error}</div>
                ) : (
                    announcements.map(announcement => (
                        <div key={announcement.id} className={`p-4 ${readAnnouncements.includes(announcement.id) ? 'bg-white' : 'bg-blue-50'}`}>
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <h3 className="text-md font-medium text-gray-900 flex items-center">
                                        {!readAnnouncements.includes(announcement.id) && (
                                            <span className="h-2 w-2 rounded-full bg-blue-600 mr-2"></span>
                                        )}
                                        {announcement.title}
                                        {announcement.importance === 'important' && (
                                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                Important
                                            </span>
                                        )}
                                    </h3>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Publié le {formatDate(announcement.publishedAt)}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setExpandedAnnouncement(expandedAnnouncement === announcement.id ? null : announcement.id);
                                        markAsRead(announcement.id);
                                    }}
                                    className="ml-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
                                >
                                    {expandedAnnouncement === announcement.id ? 'Réduire' : 'Détails'}
                                    <svg className={`ml-0.5 h-4 w-4 transition-transform ${expandedAnnouncement === announcement.id ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>

                            {expandedAnnouncement === announcement.id && (
                                <div className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                                    {announcement.content}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
